import type { Session } from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { authCallbackUrl } from './auth-callback-url.js'
import { isViewerChanged } from './auth-helpers.js'
import { clearEphemeralSession, loadEphemeralSession } from './ephemeral-session.js'
import { supabase } from './supabase.js'

/**
 * 認証コンテキスト。
 *
 * - Supabase のセッションを購読
 * - signUp / signIn / signOut のラッパー
 * - サインアップ後/サインイン後に自社 API（/auth/signup, /auth/login）を呼ぶ
 */

type AuthState = {
  session: Session | null
  loading: boolean
}

type AuthActions = {
  signUpWithEmail: (email: string, password: string) => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  updatePassword: (newPassword: string) => Promise<void>
  updateEmail: (newEmail: string) => Promise<void>
}

type AuthContextValue = AuthState & AuthActions

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  // 「session 有り & tenant_id 無し」状態の自動 onboarding を、
  // 同じ access_token で複数回試行しないためのガード。
  const autoOnboardAttemptedTokenRef = useRef<string | null>(null)
  const queryClient = useQueryClient()
  // 直前まで画面を見ていた人。undefined はまだセッションを一度も読んでいない状態
  const viewerIdRef = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    // 見ている人が変わったら、新しいセッションで描く前に取得済みのデータを捨てる。
    // 描いた後に捨てると、前の人のワークスペース名やプロジェクトが一瞬見えてしまう。
    // removeQueries ではなく resetQueries にするのは、表示中の画面が持っている結果も空に戻し、
    // 新しい人として取り直させるため（remove だと表示中の画面に古い結果が残ることがある）。
    const applySession = (s: Session | null) => {
      const nextViewerId = s?.user.id ?? null
      if (isViewerChanged(viewerIdRef.current, nextViewerId)) void queryClient.resetQueries()
      viewerIdRef.current = nextViewerId
      setSession(s)
    }
    // 初期セッション取得
    supabase.auth.getSession().then(({ data }) => {
      applySession(data.session)
      setLoading(false)
    })
    // 変化を購読
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      applySession(s)
    })
    return () => {
      subscription.unsubscribe()
    }
  }, [queryClient])

  // セーフティネット: 何らかの理由（メール確認後の root リダイレクト等）で
  // /auth/callback を経由せず session だけが立ち上がった場合に、
  // app_metadata.tenant_id が無ければ自動で onboarding を完了させる。
  // 既存ユーザーや admin は読むだけで即終了するので副作用なし。
  useEffect(() => {
    if (!session) return
    const meta = session.user.app_metadata as { tenant_id?: string; is_admin?: boolean } | undefined
    const isAdmin = meta?.is_admin === true
    const tenantId = meta?.tenant_id ?? null
    // 既に onboarding 済みなら何もしない
    if (isAdmin || tenantId) return
    // 同じ access_token で再試行しないようガード
    if (autoOnboardAttemptedTokenRef.current === session.access_token) return
    autoOnboardAttemptedTokenRef.current = session.access_token

    let cancelled = false
    ;(async () => {
      try {
        await ensureOnboardedAndLogin()
        // ensureOnboardedAndLogin 内で refreshSession 済み。
        // 新しい session は onAuthStateChange 経由で setSession され、
        // 次回 effect では tenant_id が乗っているので早期 return する。
      } catch (err) {
        if (!cancelled) {
          // 失敗時は次の session 変化（明示的な再ログイン等）まで再試行しない。
          // ユーザーには /projects 等で 401 が返ることで認知される。
          console.error('[AuthProvider] 自動 onboarding に失敗しました', err)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [session])

  const signUpWithEmail = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // メール確認が ON の場合、確認後のリダイレクト先を /auth/callback に固定する。
        // 既定だと Site URL (root) に戻ってしまい、ensureOnboardedAndLogin が呼ばれない。
        // 上のセーフティネット useEffect でも補完できるが、こちらが本筋。
        emailRedirectTo: authCallbackUrl(),
      },
    })
    if (error) throw error
    // 暫定テナントセッションがあれば migrate モードで signup を呼ぶ
    const ephemeral = loadEphemeralSession()
    await callApi('/auth/signup', 'POST', ephemeral?.token)
    // app_metadata が更新されたので JWT をリフレッシュ
    await supabase.auth.refreshSession()
    // migrate 完了したら暫定セッションを破棄
    if (ephemeral) clearEphemeralSession()
  }, [])

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    // ログイン画面はログインのみ。signup は signUpWithEmail で済んでいる前提。
    // last_login_at 更新と凍結チェックだけ走らせる。
    await callApi('/auth/login', 'POST')
    clearEphemeralSession()
  }, [])

  const signInWithGoogle = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: authCallbackUrl() },
    })
    if (error) throw error
    // リダイレクト後、コールバック画面で onAuthStateChange を経て signInWithEmail と同じ
    // ensureOnboardedAndLogin が呼ばれる必要がある（Callback ルートで実行する）
  }, [])

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }, [])

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) throw error
  }, [])

  const updateEmail = useCallback(async (newEmail: string) => {
    // リダイレクト先は第 2 引数の options で渡す（signUp と違い attributes 側には無い）。
    // 省略すると Site URL のルートに戻り、確認リンクを踏んでも変更が完了しない。
    const { error } = await supabase.auth.updateUser(
      { email: newEmail },
      { emailRedirectTo: authCallbackUrl() },
    )
    if (error) throw error
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      signUpWithEmail,
      signInWithEmail,
      signInWithGoogle,
      signOut,
      updatePassword,
      updateEmail,
    }),
    [
      session,
      loading,
      signUpWithEmail,
      signInWithEmail,
      signInWithGoogle,
      signOut,
      updatePassword,
      updateEmail,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth は AuthProvider 内で使ってください')
  return ctx
}

// ----- 内部ヘルパー -----

async function callApi(path: string, method: string, ephemeralToken?: string): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return
  const apiBase = import.meta.env.VITE_API_URL ?? '/api'
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
  if (ephemeralToken) headers['X-Ephemeral-Token'] = ephemeralToken
  const res = await fetch(`${apiBase}${path}`, { method, headers })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: { code?: string; message?: string }
    } | null
    const code = body?.error?.code ?? 'UNKNOWN'
    const message = body?.error?.message ?? `HTTP ${res.status}`
    // 動的 import は循環参照防止のため
    const { ApiError } = await import('./api.js')
    throw new ApiError(res.status, code, message)
  }
}

/**
 * オンボーディングを冪等に補完する関数。
 *
 * 用途:
 * 1. OAuth コールバック (`AuthCallbackPage`) — 初回/既存の区別が無いので冪等で吸収
 * 2. メール確認後の Site URL redirect で /auth/callback を踏まなかった場合 (`AuthProvider`
 *    のセーフティネット useEffect から呼ばれる)
 *
 * `/auth/signup` は冪等（既存なら 200, 新規なら 201）。app_metadata 反映のため
 * refreshSession を挟み、最後に `/auth/login` で last_login_at を更新する。
 */
export async function ensureOnboardedAndLogin(): Promise<void> {
  const ephemeral = loadEphemeralSession()
  // signup を呼ぶ（冪等。新規なら 201 で作成、既存なら 200）
  await callApi('/auth/signup', 'POST', ephemeral?.token)
  // app_metadata 反映のため JWT リフレッシュ
  await supabase.auth.refreshSession()
  // 最後に /auth/login で last_login_at 更新
  await callApi('/auth/login', 'POST')
  clearEphemeralSession()
}
